import React, { memo, useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Box,
  InputAdornment,
  TextField,
  Chip,
  Divider,
  LinearProgress,
  IconButton,
  Collapse,
  Skeleton,
  Alert,
  CircularProgress,
  Fade,
} from "@mui/material";
import TaskRow from "../CookPageTaskRow";
import { ingredientTasksMap } from "../../constants";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KitchenIcon from "@mui/icons-material/Kitchen";
import { useNotification } from "../../Context/notificationContext";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";

const TaskListSkeleton = () => (
  <Box sx={{ py: 1 }}>
    {[...Array(5)].map((_, i) => (
      <Box key={i} sx={{ mb: 2, display: "flex", alignItems: "center" }}>
        <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
        <Box sx={{ width: "100%" }}>
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="text" width="40%" height={20} />
        </Box>
      </Box>
    ))}
  </Box>
);

const IngredientsPanel = memo(function IngredientsPanel({
  tasks,
  inventoryData,
  onManualEntry,
  onTaskSwap,
  matchInventoryWithTasks,
  inventoryBoxes: boxes,
  loading,
  checkIfReadyToCook,
  stationSelected,
  refreshInventory,
  setIsReadyToCook,
}) {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [expanded, setExpanded] = React.useState(true);
  const { showNotification } = useNotification();
  console.log("onManualEntry Ingredient Panel", typeof onManualEntry);
  // Add state for local refresh
  const [isRefreshing, setIsRefreshing] = useState(false);
  // checkIfReadyToCook(tasks);
  const filteredTasks = useMemo(
    () =>
      tasks?.filter((task) =>
        Object.keys(ingredientTasksMap).includes(task.name)
      ) || [],
    [tasks]
  );

  if (filteredTasks.length === 0) {
  }
  const availableCount = useMemo(() => {
    return filteredTasks.filter((task) => task.isOk).length;
  }, [filteredTasks]);

  const progress =
    filteredTasks?.length > 0
      ? (availableCount / filteredTasks.length) * 100
      : 0;

  // Handle manual refresh of inventory and tasks
  const handleRefresh = () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    showNotification("info", "Refreshing inventory status...", 2000);

    if (refreshInventory && typeof refreshInventory === "function") {
      refreshInventory();

      // Reset refresh state after a reasonable timeout
      // setTimeout(() => {
      //   setIsRefreshing(false);
      // }, 1500);
    } else {
      // If no refresh function, just reset the state
      setTimeout(() => {
        setIsRefreshing(false);
      }, 500);
    }
  };

  // Separate tasks into unavailable and available
  const { unavailableTasks, availableTasks } = useMemo(() => {
    if (!filteredTasks?.length) {
      setIsReadyToCook(true);
      return { unavailableTasks: [], availableTasks: [] };
    }

    // Filter tasks that match the search term
    const searchFiltered = filteredTasks.filter((task) =>
      task.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const howManyNotTasks = searchFiltered.filter((task) => !task.isOk);
    if (howManyNotTasks.length === 0) {
      setIsReadyToCook(true);
    } else {
      setIsReadyToCook(false);
    }
    // Split into unavailable and available
    return {
      unavailableTasks: searchFiltered.filter((task) => !task.isOk),
      availableTasks: searchFiltered.filter((task) => task.isOk),
    };
  }, [filteredTasks, searchTerm]);

  // Render the task lists
  const renderTaskList = (taskList, isUnavailable = false) => {
    return taskList.map((task) => (
      <TaskRow
        key={task.id}
        tasks={tasks}
        task={task}
        onManualEntry={onManualEntry}
        onTaskSwap={onTaskSwap}
        totaltasks={tasks}
        inventoryData={inventoryData}
        matchInventoryWithTasks={matchInventoryWithTasks}
        boxes={boxes}
        stationSelected={stationSelected}
        showNotification={showNotification}
        refreshInventory={refreshInventory}
        isUnavailable={isUnavailable}
      />
    ));
  };

  // Update panel title based on tasks status
  const panelTitle = useMemo(() => {
    if (filteredTasks.length === 0) {
      setIsReadyToCook(true);
      return "No ingredients required";
    }
    if (availableCount === filteredTasks.length) {
      return "All ingredients ready";
    }
    return `Ingredients (${availableCount}/${filteredTasks.length} ready)`;
  }, [filteredTasks, availableCount]);

  return (
    <Card elevation={4} sx={{ maxWidth: "100%", overflow: "hidden" }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: { xs: "wrap", sm: "nowrap" },
            gap: 1,
          }}
        >
          <Typography variant="h6" fontWeight="medium">
            {panelTitle}
          </Typography>

          <IconButton
            onClick={handleRefresh}
            disabled={isRefreshing || loading}
            sx={{
              transition: "all 0.3s ease",
              animation: isRefreshing ? "spin 1.5s linear infinite" : "none",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          >
            {isRefreshing ? <CircularProgress size={24} /> : <RefreshIcon />}
          </IconButton>
        </Box>

        {filteredTasks.length > 0 && (
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              mb: 2,
              height: 8,
              borderRadius: 4,
              bgcolor: "background.paper",
              "& .MuiLinearProgress-bar": {
                bgcolor: progress === 100 ? "success.main" : "primary.main",
                borderRadius: 4,
                transition: "transform 0.8s ease",
              },
            }}
          />
        )}

        {loading ? (
          <TaskListSkeleton />
        ) : Array.isArray(filteredTasks) && filteredTasks.length === 0 ? (
          <Fade in timeout={500}>
            <Box sx={{ textAlign: "center", py: 3, px: 1 }}>
              <Typography
                variant="subtitle1"
                color="success.main"
                fontWeight="medium"
                gutterBottom
              >
                Ready to Cook!
              </Typography>
              <Typography variant="body2" color="textSecondary">
                No ingredients required for this recipe. You can proceed with
                cooking.
              </Typography>
            </Box>
          </Fade>
        ) : (
          <Box sx={{ maxWidth: "100%", overflow: "hidden" }}>
            {progress === 100 && (
              <Fade in timeout={500}>
                <Alert
                  severity="success"
                  sx={{
                    mb: 2,
                    wordBreak: "break-word",
                  }}
                >
                  All ingredients are ready. You can proceed with cooking.
                </Alert>
              </Fade>
            )}

            {/* Unavailable Ingredients Section */}
            {unavailableTasks.length > 0 && (
              <>
                <Box
                  sx={{
                    mb: 2,
                    mt: 2,
                    display: "flex",
                    justifyContent: "flex-start",
                  }}
                >
                  <Chip
                    icon={<ErrorOutlineIcon />}
                    label={`${unavailableTasks.length} Ingredients Need Attention`}
                    color="error"
                    variant="filled"
                    sx={{
                      fontWeight: "bold",
                      px: 1.5,
                      borderRadius: "16px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      maxWidth: "100%",
                      whiteSpace: "normal",
                      height: "auto",
                      "& .MuiChip-label": {
                        whiteSpace: "normal",
                        padding: "8px 4px",
                      },
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    maxWidth: "100%",
                    overflow: "hidden",
                    "& > *": {
                      maxWidth: "100%",
                      overflow: "visible",
                    },
                  }}
                >
                  {renderTaskList(unavailableTasks, true)}
                </Box>
                {availableTasks.length > 0 && (
                  <Divider sx={{ my: 3, borderStyle: "dashed" }} />
                )}
              </>
            )}

            {/* Available Ingredients Section */}
            {availableTasks.length > 0 && (
              <>
                <Box
                  sx={{
                    mb: 2,
                    mt: 2,
                    display: "flex",
                    justifyContent: "flex-start",
                  }}
                >
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        {`${availableTasks.length} Ready Ingredients`}
                        <EditIcon fontSize="small" />
                      </Box>
                    }
                    color="success"
                    variant="filled"
                    sx={{
                      fontWeight: "bold",
                      px: 1.5,
                      borderRadius: "16px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      maxWidth: "100%",
                      whiteSpace: "normal",
                      height: "auto",
                      "& .MuiChip-label": {
                        whiteSpace: "normal",
                        padding: "8px 4px",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      },
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    maxWidth: "100%",
                    overflow: "hidden",
                    "& > *": {
                      maxWidth: "100%",
                      overflow: "visible",
                    },
                  }}
                >
                  {renderTaskList(availableTasks, false)}
                </Box>
              </>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
});

IngredientsPanel.displayName = "IngredientsPanel";

IngredientsPanel.propTypes = {
  tasks: PropTypes.array.isRequired,
  inventoryData: PropTypes.array,
  onManualEntry: PropTypes.func.isRequired,
  onTaskSwap: PropTypes.func.isRequired,
  matchInventoryWithTasks: PropTypes.func.isRequired,
  boxes: PropTypes.array,
  loading: PropTypes.bool,
  stationSelected: PropTypes.string,
  refreshInventory: PropTypes.func,
};

export default IngredientsPanel;
